<div mat-dialog-title>
  <div class="flex items-center justify-between h-48">
    <h6>{{ !isPublished ? 'Complete your Company Information' : 'Edit Company Information' }}</h6>
    @if (isPublished) {
      <button matIconButton aria-label="Close" mat-dialog-close>
        <mat-icon>close</mat-icon>
      </button>
    }
  </div>
</div>

<mat-divider class="mx-20!" />

<form (ngSubmit)="isPublished ? updateRepairerSites() : publishRepairerSite()" [formGroup]="editAddressForm">
  <section mat-dialog-content>
    @if (isSysAdmin() && (data.repairer.unpaidLicense || ['SUSPENDED'].includes(data.repairer.status))) {
      <repairer-status-bar [repairer]="data.repairer" />
    }

    <article class="grid grid-cols-3 gap-20">
      @if (!companyName.disabled) {
        <mat-form-field [ngClass]="isSysAdmin() ? 'col-span-2' : 'col-span-full'">
          <mat-label>Company name</mat-label>
          <input formControlName="companyName" matInput required />
          @if (!companyName.valid) {
            <mat-error> Business name is <b>required</b> </mat-error>
          }
        </mat-form-field>
      } @else {
        <div [ngClass]="isSysAdmin() ? 'col-span-2' : 'col-span-full'">
          <ng-container
            *ngTemplateOutlet="
              readOnlyText;
              context: {
                label: 'Company name',
                value: data.repairer?.companyName
              }
            " />
        </div>
      }

      @if (isSysAdmin()) {
        <ng-container
          *ngTemplateOutlet="
            readOnlyText;
            context: {
              label: 'Repairer Site ID',
              value: data.repairer?.id
            }
          " />
      }
    </article>

    <article class="grid grid-cols-3 gap-x-20">
      <ng-container
        *ngTemplateOutlet="
          readOnlyText;
          context: {
            label: 'Country',
            value: data.repairer.country
          }
        " />

      <ng-container
        *ngTemplateOutlet="
          readOnlyText;
          context: {
            label: data.repairer.country === 'Australia' ? 'ABN' : 'NZBN',
            value: data.repairer.abn
          }
        " />

      @if (['ACTIVE', 'UNAVAILABLE'].includes(data.repairer.status) && isSysAdmin()) {
        <mat-form-field>
          <mat-label>Repairer Group</mat-label>
          <mat-select formControlName="repairerGroupId">
            @for (group of data?.repairersGroup; track group) {
              <mat-option [value]="group.id">
                {{ group.name }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            readOnlyText;
            context: {
              label: 'Repairer Group',
              value: data?.repairer?.repairerGroupName
            }
          " />
      }

      @if (!licenseNumber.disabled) {
        <mat-form-field>
          <mat-label>License Number</mat-label>
          <input formControlName="licenseNumber" matInput />
        </mat-form-field>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            readOnlyText;
            context: {
              label: 'License Number',
              value: data.repairer?.licenseNumber
            }
          " />
      }

      @if (!licenseRenewalDate.disabled) {
        <mat-form-field>
          <mat-label>License Start Date</mat-label>
          <input
            (keypress)="dateInputHelper.restrictDateInput($event)"
            [matDatepicker]="licenseRenewalDatePicker"
            formControlName="licenseRenewalDate"
            matInput />
          <mat-datepicker-toggle [for]="licenseRenewalDatePicker" matSuffix />
          <mat-datepicker #licenseRenewalDatePicker />
          @if (!licenseRenewalDate.valid) {
            <mat-error> The date format is <strong>DD/MM/YYYY</strong> </mat-error>
          }
        </mat-form-field>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            readOnlyText;
            context: {
              label: 'License Start Date',
              value: data.repairer?.licenseRenewalDate ? (data.repairer.licenseRenewalDate | date: 'dd/MM/yyyy') : ''
            }
          " />
      }

      @if (isSysAdmin() && !data.repairer?.quotingSystems.length) {
        <mat-form-field>
          <mat-label>Quoting System</mat-label>
          <mat-select formControlName="quotingSystem">
            @for (item of quotingSystemList; track item) {
              <mat-option [value]="item">
                {{ item }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      } @else {
        <ng-container
          *ngTemplateOutlet="
            readOnlyText;
            context: {
              label: 'Quoting System',
              value: data.repairer?.quotingSystemsString
            }
          " />
      }
    </article>

    <article class="grid grid-cols-2 gap-x-20">
      <mat-form-field>
        <mat-label>Phone Number</mat-label>
        <input formControlName="phoneNumber" matInput required />
        @if (phoneNumber.hasError('required')) {
          <mat-error> Phone number is <b>required</b> </mat-error>
        }
        @if (phoneNumber.touched && phoneNumber.hasError('phoneInvalid')) {
          <mat-error> {{ getErrorMessage(phoneNumber.errors) }} </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Email</mat-label>
        <input formControlName="email" matInput required />
        @if (email.invalid && email.hasError('required')) {
          <mat-error> Email is <strong>required</strong> </mat-error>
        }
        @if (email.invalid && email.hasError('pattern')) {
          <mat-error> Please enter a valid email address </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Primary Accounts Email</mat-label>
        <input formControlName="primaryAccountsEmail" matInput required />
        @if (primaryAccountsEmail.invalid && primaryAccountsEmail.hasError('required')) {
          <mat-error> Email is <strong>required</strong> </mat-error>
        }
        @if (primaryAccountsEmail.invalid && primaryAccountsEmail.hasError('pattern')) {
          <mat-error> Please enter a valid email address </mat-error>
        }
      </mat-form-field>

      <mat-form-field>
        <mat-label>Secondary Accounts Email</mat-label>
        <input formControlName="secondaryAccountsEmail" matInput />
        @if (secondaryAccountsEmail.invalid && secondaryAccountsEmail.hasError('pattern')) {
          <mat-error> Please enter a valid email address </mat-error>
        }
      </mat-form-field>
    </article>

    <article class="grid grid-cols-2 gap-20">
      <div>
        <div class="title-1 pb-10 text-dove-gray">Physical Address</div>
        <address-block formControlName="physicalAddress" />
      </div>
      <div>
        <div class="grid grid-cols-2 gap-20">
          <div class="title-1 pb-10 text-dove-gray">Billing Address</div>
          <mat-checkbox formControlName="sameAsPhysical" [checked]="sameAsPhysical.value">
            Same as Physical
          </mat-checkbox>
        </div>
        <address-block formControlName="billingAddress" />
      </div>
    </article>

    @if (isSysAdmin() && isPublished && data.repairer?.lastUpdaterName) {
      @let lastUpdateDate = data.repairer?.lastUpdateDate | dateepoch;
      @let lastUpdateTime = data.repairer?.lastUpdateDate | time;
      @let dateTime = `${lastUpdateDate} ${lastUpdateTime}`;
      <article class="grid grid-cols-[2fr_1fr] gap-20">
        <ng-container
          *ngTemplateOutlet="
            readOnlyText;
            context: { label: 'Last updated by', value: data.repairer?.lastUpdaterName }
          " />
        <ng-container *ngTemplateOutlet="readOnlyText; context: { label: 'Date/Time', value: dateTime }" />
      </article>
    }
  </section>

  <mat-divider class="mx-20!" />

  <mat-dialog-actions>
    @if (isPublished) {
      <button matButton="elevated" class="charcoal-button" mat-dialog-close>CANCEL</button>
    }
    @if (!isPublished) {
      <div class="basis-7/12 text-[13px]">
        Once your information is complete, publishing your location will make you available to all EstImage insurers.
      </div>
    }
    <button matButton="elevated" class="red-button" type="submit" [disabled]="editAddressForm.invalid || pending()">
      {{ isPublished ? 'SAVE' : 'PUBLISH' }}
    </button>
  </mat-dialog-actions>
</form>

<ng-template #readOnlyText let-label="label" let-value="value">
  @if (value) {
    <div class="ml-[13px] min-h-[43px] mb-15">
      <span class="body-2 block">{{ label }}</span>
      <span class="subtitle-2">{{ value ?? '' }}</span>
    </div>
  }
</ng-template>
